// src/components/sidebar/useSidebarPermissions.ts
import { computed } from 'vue'
import { getRoleConfig, type UserRole, SUPPORT_ALLOWED_ROUTES, SUPPORT_ALLOWED_CHILDREN } from './SideBarConfig'
import { platform } from '../../const'
import { getCurrentRole } from '../../services/authContext'

export function useSidebarPermissions() {

  // Détection du rôle avec impersonation
  const currentRole = computed<UserRole>(() => {
    const impersonationSession = sessionStorage.getItem('impersonation_session')
    const impersonationToken = sessionStorage.getItem('impersonation_token')
    const isImpersonating = !!(impersonationSession && impersonationToken)

    if (isImpersonating) {
      try {
        const agent = JSON.parse(impersonationSession)
        return agent.role || 'agent'
      } catch {
        // corrupted data
      }
    }
    
    return getCurrentRole() as UserRole
  })

  const isAdmin = computed(() => {
    return currentRole.value === 'admin' || currentRole.value === 'super_admin'
  })

  const isSupport = computed(() => currentRole.value === 'support')
  const isAgent = computed(() => currentRole.value === 'agent')

  const roleConfig = computed(() => getRoleConfig(currentRole.value))

  // Vérifie si un route ou ses enfants contiennent une route autorisée (pour support)
  const hasAllowedDescendant = (route: any, allowedNames: string[]): boolean => {
    // Si la route elle-même est autorisée
    if (allowedNames.includes(route.name)) return true
    
    // Si elle a des enfants, vérifier récursivement
    if (route.children && route.children.length > 0) {
      return route.children.some((child: any) => hasAllowedDescendant(child, allowedNames))
    }
    
    return false
  }

  // Filtrage des routes principales
  const filterRoutes = (routes: any[]) => {
    // Filtrer platform (users_tab)
    let filtered = routes
    if (platform !== 'cn' && platform !== 'tk') {
      filtered = filtered.filter((r) => r.name !== 'users_tab')
    }

    if (isAdmin.value) return filtered

    // Pour support : WHITELIST
    // On garde un parent si lui-même OU un de ses descendants est autorisé
    if (isSupport.value) {
      return filtered.filter((r) => hasAllowedDescendant(r, SUPPORT_ALLOWED_ROUTES))
    }

    // Pour agent : BLACKLIST
    return filtered.filter((r) => !roleConfig.value.hiddenRoutes.includes(r.name))
  }

  // Filtrage des enfants
  const filterChildren = (children?: any[]) => {
    if (!children) return []

    if (isAdmin.value) {
      return children.filter((child) => {
        if (child.name === 'vip-setting') return false
        if (child.name === 'promotion-link') return false
        return true
      })
    }

    // Pour support : WHITELIST
    if (isSupport.value) {
      return children.filter((child) => {
        // Garde le child si son nom est dans la whitelist
        // OU si c'est un parent qui contient des enfants autorisés
        const isAllowed = SUPPORT_ALLOWED_CHILDREN.includes(child.name)
        const hasAllowedKids = child.children && child.children.some((c: any) => 
          SUPPORT_ALLOWED_CHILDREN.includes(c.name)
        )
        return isAllowed || hasAllowedKids
      })
    }

    // Agent : BLACKLIST
    return children.filter((child) => {
      if (child.name === 'vip-setting') return true
      if (child.name === 'promotion-link') return true
      return !roleConfig.value.hiddenChildren.includes(child.name)
    })
  }

  // Mapping des noms de routes
  const getRouteName = (navigationRouteName: string): string => {
    if (isAdmin.value) {
      return roleConfig.value.routeMappings[navigationRouteName] || navigationRouteName
    }
    if (isSupport.value) {
      return navigationRouteName
    }
    return roleConfig.value.routeMappings[navigationRouteName] || navigationRouteName
  }

  // Override des display names
  const getDisplayName = (route: any): string => {
    if (route.name === 'announcements') {
      return isAdmin.value ? 'menu.notice_management' : 'menu.system_announcements'
    }
    if (route.name === 'withdraw') {
    return isAdmin.value ? 'menu.withdraw' : 'menu.withdrawAgent'
  }
    return route.displayName
  }

  return {
    currentRole,
    isAdmin,
    isSupport,
    isAgent,
    filterRoutes,
    filterChildren,
    getRouteName,
    getDisplayName
  }
}